/**
 * cart-invoice
 *
 * Campi per la richiesta di fattura nel carrello (snippets/cart-invoice.liquid),
 * salvati come attributi del carrello con una POST su /cart/update.js, come fa
 * `cart-note` per la nota. Gli input non hanno `name` ne' `form=`: il drawer viene
 * ridisegnato via innerHTML a ogni mutazione del carrello, quindi il valore va
 * salvato subito e ri-renderizzato da `cart.attributes`, non affidato al form.
 *
 * Gli attributi sono letti dal gestionale: nomi e valori non vanno cambiati.
 * Nessuna stringa tradotta qui: i messaggi sono resi dallo snippet e nascosti
 * con `hidden`, questo file li mostra.
 */
if (!customElements.get('cart-invoice')) {
  const CART_INVOICE_FIELDS = ['_fiscalCode', '_vatNumber', '_companyName'];

  // Stato condiviso fra le istanze. Drawer e pagina carrello possono convivere
  // sulla stessa pagina, e un'istanza ridisegnata nasce dall'HTML del server, che
  // puo' essere piu' vecchio dell'ultimo salvataggio: l'ultimo stato inviato vince.
  const cartInvoiceStore = {
    state: null,
    queue: Promise.resolve(),
    pending: 0,
    failed: false,
  };

  class CartInvoice extends HTMLElement {
    constructor() {
      super();
      this.bypassSubmit = false;
      this.onSubmit = this.onSubmit.bind(this);
      this.onSync = this.onSync.bind(this);
      this.addEventListener('change', this.onChange.bind(this));
      this.addEventListener('input', this.onInput.bind(this));
    }

    connectedCallback() {
      this.requestInput = this.querySelector('[data-invoice-request]');
      this.fieldsElement = this.querySelector('[data-invoice-fields]');
      this.typeSelect = this.querySelector('[data-invoice-type]');
      this.fieldElements = Array.from(this.querySelectorAll('[data-invoice-field]'));
      this.saveErrorElement = this.querySelector('[data-invoice-save-error]');

      if (!this.requestInput || !this.fieldsElement || !this.typeSelect) return;

      if (cartInvoiceStore.state) this.applyState(cartInvoiceStore.state);

      // Il guard sta su document in fase di capture: il form di checkout viene
      // ricreato a ogni ridisegno del drawer, e un listener agganciato al form
      // resterebbe su un nodo morto.
      document.addEventListener('submit', this.onSubmit, true);
      document.addEventListener('cart-invoice:sync', this.onSync);
    }

    disconnectedCallback() {
      document.removeEventListener('submit', this.onSubmit, true);
      document.removeEventListener('cart-invoice:sync', this.onSync);
    }

    get requested() {
      return this.requestInput.checked;
    }

    get customerType() {
      return this.typeSelect.value;
    }

    onChange(event) {
      if (event.target === this.requestInput && !this.requested) this.clearErrors();

      this.updateVisibility();
      this.clearFieldError(event.target);
      this.save();
    }

    onInput(event) {
      this.clearFieldError(event.target);
    }

    onSync(event) {
      if (event.detail.source === this) return;
      this.applyState(event.detail.state);
    }

    updateVisibility() {
      this.fieldsElement.hidden = !this.requested;
      this.querySelectorAll('[data-invoice-group]').forEach((group) => {
        group.hidden = group.dataset.invoiceGroup !== this.customerType;
      });
    }

    /**
     * Il payload si costruisce sempre per intero dallo stato del DOM. Gli
     * attributi non pertinenti (fattura non richiesta, gruppo non attivo) partono
     * come stringa vuota, che per Shopify vuol dire rimuovi l'attributo: cosi'
     * sull'ordine non restano i dati di un tipo cliente scelto e poi cambiato.
     */
    buildState() {
      const state = { _invoiceRequired: 'false', _invoiceType: '' };
      CART_INVOICE_FIELDS.forEach((name) => {
        state[name] = '';
      });

      if (!this.requested) return state;

      state._invoiceRequired = 'true';
      state._invoiceType = this.customerType;

      this.fieldElements.forEach((field) => {
        const group = field.closest('[data-invoice-group]');
        if (group.dataset.invoiceGroup !== this.customerType) return;
        state[field.dataset.invoiceField] = field.value.trim();
      });

      return state;
    }

    applyState(state) {
      this.requestInput.checked = state._invoiceRequired === 'true';
      this.typeSelect.value = state._invoiceType;
      this.fieldElements.forEach((field) => {
        field.value = state[field.dataset.invoiceField] || '';
      });
      this.updateVisibility();
    }

    /**
     * I salvataggi si accodano invece di partire in parallelo: due POST
     * concorrenti possono arrivare fuori ordine, e a vincere sarebbe la piu'
     * vecchia. La coda non va mai in rejection: l'esito sta in `failed`.
     */
    save() {
      const state = this.buildState();
      cartInvoiceStore.state = state;
      document.dispatchEvent(new CustomEvent('cart-invoice:sync', { detail: { state, source: this } }));

      const body = JSON.stringify({ attributes: state });
      cartInvoiceStore.pending += 1;
      cartInvoiceStore.queue = cartInvoiceStore.queue
        .then(() => fetch(`${routes.cart_update_url}.js`, { ...fetchConfig(), ...{ body } }))
        .then((response) => {
          if (!response.ok) throw new Error(response.status);
          cartInvoiceStore.failed = false;
          this.saveErrorElement.hidden = true;
        })
        .catch(() => {
          cartInvoiceStore.failed = true;
        })
        .finally(() => {
          cartInvoiceStore.pending -= 1;
        });

      return cartInvoiceStore.queue;
    }

    /**
     * Il gestionale si aspetta `_invoiceRequired: "false"` quando la fattura non
     * e' richiesta: se il cliente non ha mai toccato la checkbox, il carrello non
     * ha ancora l'attributo e va scritto prima di uscire verso il checkout.
     */
    needsSave() {
      if (cartInvoiceStore.pending > 0 || cartInvoiceStore.failed) return true;
      return !cartInvoiceStore.state && !this.requested && this.dataset.savedRequired !== 'false';
    }

    onSubmit(event) {
      if (this.bypassSubmit) return;

      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.id !== this.dataset.formId) return;

      const submitter = event.submitter;
      if (!submitter || submitter.name !== 'checkout') return;

      if (!this.validate()) {
        event.preventDefault();
        const firstInvalid = this.querySelector('[aria-invalid="true"]');
        if (firstInvalid) firstInvalid.focus();
        return;
      }

      if (!this.needsSave()) return;

      event.preventDefault();
      const saved = cartInvoiceStore.pending > 0 ? cartInvoiceStore.queue : this.save();

      saved.then(() => {
        // Andare al checkout con il salvataggio fallito vorrebbe dire perdere i
        // dati della fattura proprio quando servono: meglio fermarsi.
        if (cartInvoiceStore.failed) {
          this.saveErrorElement.hidden = false;
          return;
        }

        // Nel frattempo il drawer puo' essere stato ridisegnato: si risottomette
        // il form vivo, con il bottone di checkout vivo.
        const liveForm = document.getElementById(form.id);
        const liveSubmitter = submitter.isConnected
          ? submitter
          : document.querySelector(`[name="checkout"][form="${form.id}"]`);
        if (!liveForm || !liveSubmitter) return;

        this.bypassSubmit = true;
        liveForm.requestSubmit(liveSubmitter);
        this.bypassSubmit = false;
      });
    }

    validate() {
      this.clearErrors();
      if (!this.requested) return true;

      if (!this.customerType) {
        this.showFieldError(this.typeSelect);
        return false;
      }

      let valid = true;
      const group = this.querySelector(`[data-invoice-group="${this.customerType}"]`);
      group.querySelectorAll('[data-invoice-field]').forEach((field) => {
        if (field.value.trim()) return;
        this.showFieldError(field);
        valid = false;
      });

      return valid;
    }

    getFieldError(field) {
      const wrapper = field.closest('[data-invoice-control]');
      return wrapper && wrapper.querySelector('[data-invoice-error]');
    }

    showFieldError(field) {
      const error = this.getFieldError(field);
      field.setAttribute('aria-invalid', 'true');
      if (!error) return;
      error.hidden = false;
      field.setAttribute('aria-describedby', error.id);
    }

    clearFieldError(field) {
      if (field !== this.typeSelect && !this.fieldElements.includes(field)) return;

      const error = this.getFieldError(field);
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
      if (error) error.hidden = true;
    }

    clearErrors() {
      [this.typeSelect, ...this.fieldElements].forEach((field) => this.clearFieldError(field));
    }
  }

  customElements.define('cart-invoice', CartInvoice);
}
